import { Box, Flex, Text } from '@chakra-ui/react';
import { Switch, Select, type SelectOption } from '@kula-ai/components';
import { Stars03, LayoutLeft } from '@kula-ai/icons-core';
import { PenFileAsset } from '@kula-ai/assets';
import {
  CUSTOM_TEMPLATE_LABEL,
  customTemplateSelectProps,
} from '@/components/shared/customTemplateLabel';
import type { ScorecardStepCopy } from './ScorecardStep.types';

// Brand gradient used for the AI pill text + the active switch track.
// Matches the Figma reference for the "Fill with AI" affordance.
const AI_GRADIENT = 'linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)';

interface ScorecardStepHeaderProps {
  scorecardEnabled: boolean;
  onScorecardEnabledChange: (_enabled: boolean) => void;
  aiEnabled: boolean;
  onAiEnabledChange: (_enabled: boolean) => void;
  showAiToggle?: boolean;
  templateOptions: SelectOption[];
  selectedTemplate: string | null;
  hasQuestions?: boolean;
  onTemplateChange: (_value: string | null) => void;
  /** Relabels the closed picker once the questions leave the template. */
  templateEdited?: boolean;
  copy: ScorecardStepCopy;
}

export const ScorecardStepHeader = ({
  scorecardEnabled,
  onScorecardEnabledChange,
  aiEnabled,
  onAiEnabledChange,
  showAiToggle = true,
  templateOptions,
  selectedTemplate,
  hasQuestions = false,
  onTemplateChange,
  templateEdited = false,
  copy,
}: ScorecardStepHeaderProps) => (
  <Flex flexDir="column" gap="18px" w="100%">
    <Flex
      align="center"
      justify="space-between"
      gap="3"
      p="3"
      bg="secondary-25"
      borderRadius="6px"
    >
      <Flex align="center" gap="3" minW={0}>
        <PenFileAsset width={34} height={45} />
        <Text
          fontSize="sm"
          fontWeight={550}
          lineHeight="150%"
          color="secondary-900"
        >
          {copy.enableLabel}
        </Text>
      </Flex>
      <Switch
        checked={scorecardEnabled}
        onCheckedChange={onScorecardEnabledChange}
      />
    </Flex>

    {scorecardEnabled && (
      <>
        {showAiToggle && (
          // Two-tier layout from Figma:
          //   row 1: [pill: icon + gradient label] —— divider —— [switch]
          //   row 2: supporting text (outside the pill)
          <Flex flexDir="column" gap="1.5" pl="0" pr="1" py="1.5">
            <Flex align="center" gap="3" w="100%" minH="30px">
              {/* Pill — icon + gradient title */}
              <Flex
                align="center"
                gap="6px"
                p="3px 6px"
                bg="#F9F5FF"
                borderRadius="6px"
              >
                <Stars03
                  size={18}
                  gradient={{
                    direction: 'vertical',
                    colors: [
                      { offset: '0%', color: '#797DFF' },
                      { offset: '100%', color: '#680AF5' },
                    ],
                  }}
                />
                <Text
                  fontSize="14px"
                  fontWeight={550}
                  lineHeight="24px"
                  bgGradient={AI_GRADIENT}
                  bgClip="text"
                  sx={{ WebkitTextFillColor: 'transparent' }}
                >
                  {copy.aiLabel}
                </Text>
              </Flex>
              {/* Divider — fills remaining width between pill and switch */}
              <Box flex="1" h="1px" bg="#E9EBF4" />
              {/* Switch — gradient track when checked */}
              <Box
                sx={{
                  '& button[data-state="checked"]': {
                    background: AI_GRADIENT,
                    borderColor: 'transparent',
                  },
                }}
              >
                <Switch
                  size="sm"
                  checked={aiEnabled}
                  onCheckedChange={onAiEnabledChange}
                />
              </Box>
            </Flex>
            <Text
              fontSize="14px"
              fontWeight={450}
              lineHeight="150%"
              color="#6D659D"
            >
              {copy.aiDescription}
            </Text>
          </Flex>
        )}

        <Select
          options={templateOptions}
          {...customTemplateSelectProps(templateEdited)}
          value={selectedTemplate ?? undefined}
          onChange={(val) => onTemplateChange((val as string) || null)}
          // Nothing selected but questions present means a custom scorecard;
          // the trigger relabel only runs on a selected option.
          placeholder={
            !selectedTemplate && hasQuestions
              ? CUSTOM_TEMPLATE_LABEL
              : copy.templatePlaceholder
          }
          beforeElement={
            <LayoutLeft
              size={18}
              className="-mr-2 ml-1 shrink-0 text-secondary-500"
              aria-hidden
            />
          }
        />
      </>
    )}
  </Flex>
);
